// Theme provider for the MCM cooking-app experiment.
//
// The provider resolves the active palette by CONTINUOUSLY
// interpolating between the four hour-anchored themes in
// themes.js. A fractional hour (e.g. 6.75 for 6:45am) drives the
// blend, so every minute of the day produces a slightly different
// rendered theme — 6:45 doesn't look identical to 4:30.
//
// Modes:
//   - "auto"   → follow the wall clock, re-resolve every minute
//   - "hour"   → override to a specific fractional hour (slider)
//
// Primitives consume `useTheme()` to read the fully-blended
// { color, shadow, glassPanel, ctaButton, ghostButton, backdrop }
// — none of the interpolation logic leaks into screens.
//
// Smooth cross-fades between successive blended values still
// flow through the shared THEME_TRANSITION CSS rule. With
// continuous blending the transition mostly smooths out the
// minute-tick updates on auto mode; during slider drag the
// per-frame re-renders happen faster than the transition and
// the browser just keeps up.

import {
  createContext, useContext, useEffect, useMemo, useState,
} from "react";
import {
  THEMES, THEME_ORDER, currentHour, resolveThemeAtHour,
  glassPanelFor, ctaButtonFor, ghostButtonFor,
} from "./themes";
import { blendThemes } from "./themeBlend";

const ThemeCtx = createContext(null);

export function ThemeProvider({ children }) {
  // override: null → auto (wall clock); number → pinned fractional hour
  const [override, setOverride] = useState(null);
  // Tick state used to force a re-resolve on auto mode. Value is
  // irrelevant — setting it triggers the render that recomputes
  // the hour from the fresh Date.
  const [, setTick] = useState(0);

  useEffect(() => {
    if (override !== null) return;
    // Re-resolve once a minute so the auto theme drifts with the
    // clock. One minute is fine-grained enough for the blend to
    // feel alive without wasting cycles.
    const id = setInterval(() => setTick((t) => t + 1), 60 * 1000);
    return () => clearInterval(id);
  }, [override]);

  const hour = override == null ? currentHour() : override;
  const theme = useMemo(
    () => resolveThemeAtHour(hour, blendThemes),
    [hour]
  );

  const value = useMemo(() => ({
    theme,
    hour,
    isAuto:     override == null,
    setHour:    (h) => setOverride(h),
    clearHour:  () => setOverride(null),
    glassPanel: glassPanelFor(theme),
    ctaButton:  ctaButtonFor(theme),
    ghostButton: ghostButtonFor(theme),
  }), [theme, hour, override]);

  return (
    <ThemeCtx.Provider value={value}>
      <ThemeTransitionShell theme={theme}>
        {children}
      </ThemeTransitionShell>
    </ThemeCtx.Provider>
  );
}

// --- Cross-fade wrapper -------------------------------------------------

// Pull the FIRST color out of a CSS background string. Works for the
// gradient form `linear-gradient(180deg, #F9EFDC 0%, #F1E3C6 100%)`
// (matches `#F9EFDC`) and the post-blend form
// `linear-gradient(180deg, rgba(249,239,220,1) 0%, ...)` (matches
// the first rgba() chunk). That first stop is exactly what paints
// the top of the viewport, which is where iOS lays its status bar —
// so it's the right value to feed into `<meta name="theme-color">`.
const FIRST_COLOR_RE = /rgba?\([^)]+\)|#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}/;
function topColorOfGradient(gradient) {
  if (!gradient || typeof gradient !== "string") return null;
  const m = gradient.match(FIRST_COLOR_RE);
  return m ? m[0] : null;
}

// Extend the gradient's TOP color flat through the iOS safe-area
// zone PLUS a small buffer below it. Without this, the visible page
// just below the status bar shows a gradient color a few % into the
// slope while iOS paints the status-bar zone in pure gradient 0%,
// producing a subtle boundary band. The buffer (BLOB_FADE_PX) also
// matches the WarmBackdrop's mask fade region so decorations don't
// tint the top strip — see primitives.jsx.
//
// On desktop (no safe area) env() returns 0px, so the flat region
// is just the buffer — still cleaner than a hard 0% slope start.
const FIRST_STOP_RE =
  /(rgba?\([^)]+\)|#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3})(\s+0%\s*,)/;
// Exported so primitives.jsx (WarmBackdrop) can mask its decorations
// through the same vertical zone — keeps the flat-color strip and
// the no-decorations strip in lockstep at one source of truth.
export const FLAT_TOP_BUFFER_PX = 40;
function flattenGradientForSafeArea(gradient) {
  if (!gradient || typeof gradient !== "string") return gradient;
  const m = gradient.match(FIRST_STOP_RE);
  if (!m) return gradient;
  const color = m[1];
  return gradient.replace(
    FIRST_STOP_RE,
    `${color} 0%, ${color} calc(env(safe-area-inset-top, 0px) + ${FLAT_TOP_BUFFER_PX}px),`,
  );
}

function ThemeTransitionShell({ theme, children }) {
  // The gradient lives on the shell wrapper itself (NOT on a separate
  // position:fixed layer). Two reasons that matter:
  //
  // 1. backdrop-filter on every GlassPanel only blurs content INSIDE
  //    its own stacking context. A position:fixed sibling with
  //    z-index:0 sits in a different stacking context, so glass cards
  //    end up blurring transparent space — frosted-glass effect goes
  //    flat. Painting the gradient on the same wrapper that contains
  //    the panels keeps the blur looking right.
  // 2. background-attachment: fixed pins the gradient to the VIEWPORT
  //    instead of the (content-sized) wrapper. So however far the
  //    user scrolls, gradient 0% stays at viewport y=0 — matching the
  //    iOS status-bar zone that <meta theme-color> paints — and 100%
  //    stays at viewport y=bottom. Previously the gradient stretched
  //    across the full scroll height and slid with scroll, which is
  //    what produced the meta/page mismatch.
  //
  // Body + meta theme-color stay locked to the gradient's TOP stop
  // (the exact pixel color the gradient's 0% renders) so the iOS
  // safe-area zone matches.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const gradient = theme.backdrop.base;
    if (!gradient) return;
    const topColor = topColorOfGradient(gradient) || theme.color.cream;
    document.body.style.background = topColor;
    document.body.style.backgroundAttachment = "";
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta && topColor) meta.setAttribute("content", topColor);
  }, [theme.backdrop.base, theme.color.cream]);

  // Top-edge treatment is now a flat extension of the gradient's
  // top color. The previous glass cap (a position:fixed blurred
  // overlay covering env(safe-area-inset-top) + 40px) was painting
  // a visible blur band at the top of the screen — fine when the
  // iOS PWA was launching with Safari chrome, distracting once the
  // app went standalone with status-bar-style="black-translucent"
  // and the cream theme-color paints the full safe-area zone. The
  // gradient flattening (flattenGradientForSafeArea) already keeps
  // the top strip a solid color matching the meta theme-color, so
  // there's nothing for a blur cap to fix. Removed.
  // Long-hand background props throughout — React warns when you mix
  // the `background` shorthand with `backgroundAttachment` because
  // the shorthand resets the long-hand on every re-render. Splitting
  // into `backgroundImage` + `backgroundAttachment` + `backgroundColor`
  // keeps the warning from firing AND avoids the actual styling bug
  // it warns about (theme transitions briefly losing the attachment
  // and snapping the gradient to scroll).
  return (
    <div
      style={{
        minHeight: "100dvh",
        backgroundImage: flattenGradientForSafeArea(theme.backdrop.base),
        backgroundAttachment: "fixed",
        backgroundRepeat: "no-repeat",
        backgroundSize: "100% 100%",
        color: theme.color.ink,
        transition: "background-image 700ms ease, color 700ms ease",
        position: "relative",
      }}
    >
      {children}
    </div>
  );
}

// --- Consumer hook ------------------------------------------------------

export function useTheme() {
  const ctx = useContext(ThemeCtx);
  if (!ctx) {
    // Fallback for isolated renders — resolve at the wall clock
    // using the day theme as a safe anchor pair. Primitives still
    // render something sensible even without a provider.
    const fallback = THEMES.day;
    return {
      theme: fallback,
      hour: currentHour(),
      isAuto: true,
      setHour: () => {},
      clearHour: () => {},
      glassPanel: glassPanelFor(fallback),
      ctaButton:  ctaButtonFor(fallback),
      ghostButton: ghostButtonFor(fallback),
    };
  }
  return ctx;
}

// --- Shared transition rule ---------------------------------------------

// Spread into every primitive that paints theme-dependent color
// props. With continuous blending the rule mostly smooths between
// auto-mode minute ticks; during slider scrub React re-renders
// faster than the transition, so the browser interpolates each
// successive value naturally.
export const THEME_TRANSITION = {
  transition:
    "background 700ms ease," +
    "background-color 700ms ease," +
    "color 700ms ease," +
    "border-color 700ms ease," +
    "box-shadow 700ms ease",
};

// --- Re-exports --------------------------------------------------------

export { THEMES, THEME_ORDER, currentHour, resolveThemeAtHour };
